import { inject, Injectable } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';

import { FormlyService } from './formly.service';
import { ModalService } from '../modal.service';
import { BaseInlineFormComponent } from '../../components/base-inline-form/base-inline-form.component';
import { GridComponent } from '../../components/grids/grid/grid.component';
import { FormOkCancelModalComponent } from '../../components/modals/form-ok-cancel-modal/form-ok-cancel-modal.component';
import { OkCancelModalComponent } from '../../components/modals/ok-cancel-modal/ok-cancel-modal.component';
import { ignoreModalError } from '../../helpers';

@Injectable({ providedIn: 'root' })
export class GridLayoutFormService {
  private readonly formlyService = inject(FormlyService);
  private readonly modalService = inject(ModalService);

  // loadNow: false is for a host that builds its fields before the grid's stateKey is bound (app-tab-grid
  // in ngOnInit) and loads the layouts itself afterwards. Asked with no key, the search answers with
  // every preference the user can see, which could land after the real list and replace it.
  createLayoutField(config: {
    grid: GridComponent;
    form?: BaseInlineFormComponent;
    model: any;
    loadNow?: boolean;
  }): FormlyFieldConfig {
    const { grid, form, model, loadNow = true } = config;

    if (loadNow) {
      grid.loadLayouts().subscribe();
    }

    return this.formlyService.select('layout', $localize`Layout`, grid.layoutsOptions$, {
      change: (field: FormlyFieldConfig) => {
        grid.applyLayout(field.model.layout);
        grid.updateCurrentLayout()?.subscribe();
      },
      create: () => {
        const { modal, component } =
          this.modalService.openMedium<FormOkCancelModalComponent>(FormOkCancelModalComponent);

        component.header.set($localize`New Layout`);
        component.fields.set(grid.getLayoutFields());
        modal.result.then((result) => {
          grid.saveCurrentStateAsLayout(result.global, result.name).subscribe((layoutId: string) => {
            model.layout = layoutId;
            form?.updateModel({ layout: layoutId });
            grid.updateCurrentLayout()?.subscribe();
          });
        }, ignoreModalError);
      },
      update: () => {
        const { modal, component } = this.modalService.openMedium<OkCancelModalComponent>(OkCancelModalComponent);

        component.header.set($localize`Update Layout`);
        component.message.set($localize`Are you sure you want to update this layout?`);

        modal.result.then(() => grid.updateCurrentLayout()?.subscribe(), ignoreModalError);
      },
      delete: () => {
        const layoutId = form?.model()?.layout ?? model.layout;

        const { modal, component } = this.modalService.openMedium<OkCancelModalComponent>(OkCancelModalComponent);

        component.header.set($localize`Delete Layout`);
        component.message.set($localize`Are you sure you want to delete this layout?`);

        modal.result.then(
          () =>
            grid.deleteLayout(layoutId)?.subscribe(() => {
              const modelValue = form?.model();
              if (modelValue) {
                modelValue.layout = undefined;
                form.updateModel({ layout: undefined });
              } else {
                delete model.layout;
              }
              grid.updateCurrentLayout()?.subscribe();
              grid.applyLayout();
            }),
          ignoreModalError,
        );
      },
      showDelete: () => grid.canDeleteLayout(form?.model()?.layout ?? model.layout),
    });
  }
}
